import {
    DeleteMenuItem,
    EditButton,
    PublishButton,
    SaveAsButton,
} from './Buttons';
import { useCallback, useMemo } from 'react';
import {
    useCurrentCountry,
    useFetchWatchlistItems,
    useFetchWatchlists,
    useMutateWatchlistItems,
    useTikTokWeeks,
} from 'utils/hooks';
import WatchlistView from './WatchlistView';
import { getInstagramHashtagsByName } from 'utils/api/instagram.api';
import { getTikTokHashtagsAnyContext } from 'utils/api/tiktok.api';
import { useParams } from 'react-router-dom';
import { v5 } from 'uuid';

const WATCHLIST_NAMESPACE = '6ba7b810-9dad-11d1-80b4-00c04fd430c8';

function Watchlist() {
    const { watchlistId } = useParams();
    const country = useCurrentCountry();
    const { week, year } = useTikTokWeeks();
    const { isLoading: isLoadingWatchlists, data: watchlists } =
        useFetchWatchlists();
    const {
        isLoading: isLoadingWatchlistItems,
        isFetching: isFetchingWatchlistItems,
        data: allWatchedItems,
    } = useFetchWatchlistItems();
    const watchlistItems = useMemo(
        () =>
            allWatchedItems
                ? allWatchedItems.filter(
                      ({ watchlist: currentWatchlistId }) =>
                          currentWatchlistId === watchlistId,
                  )
                : null,
        [allWatchedItems, watchlistId],
    );

    const { add, remove } = useMutateWatchlistItems();

    const getContextDataFromMatch = (match, fallbackContextData) => {
        if (match?.brand) return { brandId: match.brand };
        if (match?.category) return { categoryId: match.category };
        return fallbackContextData;
    };

    const onAddItem = async ({ id, name, type, brandId, categoryId }) => {
        if (type === 'hashtag') {
            // Both TikTok and Instagram hashtags are of type 'hashtag' when added from algolia search
            const fallbackContextData = {
                ...(brandId ? { brandId } : {}),
                ...(categoryId && !brandId ? { categoryId } : {}),
            };

            if (!country?.id) {
                const hashtagId = v5(name, WATCHLIST_NAMESPACE);
                add([
                    {
                        id: hashtagId,
                        watchlist: watchlistId,
                        data: { name, type, ...fallbackContextData },
                    },
                ]);
                return;
            }

            try {
                const tiktokHashtagsQuery =
                    week && year
                        ? getTikTokHashtagsAnyContext({
                              data: {
                                  country: [country.id],
                                  hashtag: [name],
                                  year: [year],
                                  week: [week],
                                  page: 1,
                                  per_page: 25,
                              },
                          }).then(({ data }) => data || [])
                        : Promise.resolve([]);
                const instagramHashtagsQuery = getInstagramHashtagsByName({
                    data: {
                        hashtags: [name],
                        country: [country.id],
                        include_context: true,
                        page: 1,
                        per_page: 25,
                    },
                }).then(({ data }) => data || []);

                const [tiktokHashtags, instagramHashtags] = await Promise.all([
                    tiktokHashtagsQuery,
                    instagramHashtagsQuery,
                ]);

                const tiktokMatch =
                    tiktokHashtags.find(
                        (item) => item.brand || item.category,
                    ) || tiktokHashtags[0];
                const instagramMatch =
                    instagramHashtags.find(
                        (item) => item.brand || item.category,
                    ) || instagramHashtags[0];
                const itemsToAdd = [];

                if (tiktokHashtags.length > 0) {
                    itemsToAdd.push({
                        id: v5(name, WATCHLIST_NAMESPACE),
                        watchlist: watchlistId,
                        data: {
                            name,
                            type: 'hashtag',
                            ...getContextDataFromMatch(
                                tiktokMatch,
                                fallbackContextData,
                            ),
                        },
                    });
                }

                if (instagramHashtags.length > 0) {
                    itemsToAdd.push({
                        id: v5(`instagram-${name}`, WATCHLIST_NAMESPACE),
                        watchlist: watchlistId,
                        data: {
                            name,
                            type: 'instagram-hashtag',
                            ...getContextDataFromMatch(
                                instagramMatch,
                                fallbackContextData,
                            ),
                        },
                    });
                }

                // Add the items that have results
                if (itemsToAdd.length > 0) {
                    add(itemsToAdd);
                } else {
                    console.warn(
                        `No results found for hashtag "${name}" in either TikTok or Instagram`,
                    );
                }
            } catch (error) {
                console.error('Error checking hashtag availability:', error);
                // Fallback to original behavior if API calls fail adding just tiktok hashtag
                const hashtagId = v5(name, WATCHLIST_NAMESPACE);
                add([
                    {
                        id: hashtagId,
                        watchlist: watchlistId,
                        data: { name, type, ...fallbackContextData },
                    },
                ]);
            }
        } else {
            add([{ id, watchlist: watchlistId, data: { name, type } }]);
        }
    };

    const onRemoveItem = useCallback(
        (node) => {
            if (node.type === 'hashtag' || node.type === 'instagram-hashtag') {
                const itemsToRemove = watchlistItems.filter(
                    ({ name, type, categoryId, brandId }) => {
                        const nameMatch = node.name === name;
                        const isHashtagType =
                            type === 'hashtag' || type === 'instagram-hashtag';
                        return nameMatch && isHashtagType;
                    },
                );
                if (itemsToRemove.length > 0) remove(itemsToRemove);
            } else if (node.type === 'related-hashtag') {
                const itemsToRemove = watchlistItems.filter(
                    ({ id, name, type, categoryId, brandId }) => {
                        const nameMatch = node.name === name;
                        const typeMatch = type === node.type;
                        if (node.categoryId && node.brandId) {
                            return (
                                nameMatch &&
                                typeMatch &&
                                categoryId &&
                                brandId &&
                                node.categoryId === categoryId &&
                                node.brandId === brandId
                            );
                        } else if (node.categoryId && !node.brandId) {
                            return (
                                nameMatch &&
                                typeMatch &&
                                categoryId &&
                                !brandId &&
                                node.categoryId === categoryId
                            );
                        } else if (node.brandId && !node.categoryId) {
                            return (
                                nameMatch &&
                                typeMatch &&
                                brandId &&
                                !categoryId &&
                                node.brandId === brandId
                            );
                        }
                        return false;
                    },
                );
                if (itemsToRemove.length > 0) remove(itemsToRemove);
            } else if (node.type === 'related-category') {
                // Row id may come from the BE response (data-store row UUID,
                // for items rendered in the TT tab) and won't match the saved
                // item's id (a derived `uuidv5(parent, relatedCategoryId)`).
                // Match by the natural key instead: (parent, relatedCategoryId,
                // type). Pop Index items also match here since their saved
                // shape is the same.
                const itemsToRemove = watchlistItems.filter(
                    ({ type, categoryId, brandId, relatedCategoryId }) => {
                        if (type !== node.type) return false;
                        if (
                            !relatedCategoryId ||
                            relatedCategoryId !== node.relatedCategoryId
                        ) {
                            return false;
                        }
                        if (node.categoryId) {
                            return (
                                !!categoryId && categoryId === node.categoryId
                            );
                        }
                        if (node.brandId) {
                            return !!brandId && brandId === node.brandId;
                        }
                        return false;
                    },
                );
                if (itemsToRemove.length > 0) remove(itemsToRemove);
            } else if (node.type === 'instagram-related-hashtag') {
                const itemsToRemove = watchlistItems.filter(
                    ({ id, name, type, categoryId, brandId }) => {
                        const nameMatch = node.name === name;
                        const typeMatch = type === node.type;
                        if (node.category && node.brand) {
                            return (
                                nameMatch &&
                                typeMatch &&
                                categoryId &&
                                brandId &&
                                node.category === categoryId &&
                                node.brand === brandId
                            );
                        } else if (node.category && !node.brand) {
                            return (
                                nameMatch &&
                                typeMatch &&
                                categoryId &&
                                !brandId &&
                                node.category === categoryId
                            );
                        } else if (node.brand && !node.category) {
                            return (
                                nameMatch &&
                                typeMatch &&
                                brandId &&
                                !categoryId &&
                                node.brand === brandId
                            );
                        }
                        return false;
                    },
                );
                if (itemsToRemove.length > 0) remove(itemsToRemove);
            } else {
                const maybeItem = watchlistItems.find(
                    ({ id }) => node.id === id,
                );
                if (maybeItem) remove([maybeItem]);
            }
        },
        [watchlistItems, remove],
    );

    const watchlist =
        watchlists && watchlists[watchlistId] ? watchlists[watchlistId] : null;

    return (
        <WatchlistView
            watchlist={watchlist}
            watchlistId={watchlistId}
            watchlistItems={watchlistItems}
            isLoadingWatchlists={isLoadingWatchlists}
            isLoadingWatchlistItems={isLoadingWatchlistItems}
            isFetchingWatchlistItems={isFetchingWatchlistItems}
            onAddItem={onAddItem}
            onRemoveItem={onRemoveItem}
            menuItems={
                watchlist
                    ? {
                          edit: EditButton,
                          shareLink: PublishButton,
                          duplicate: SaveAsButton,
                          delete: DeleteMenuItem,
                      }
                    : null
            }
        />
    );
}

export default Watchlist;
